import { Clock, Mail, MapPin, Phone } from "lucide-react";
import { formatTime, telHref } from "@/lib/api";
import { SectionHead } from "@/components/site/InfoSections";
import { MessageForm } from "@/components/site/MessageForm";

const Hours = ({ s }) =>
  s.hours_mode === "appointment" ? (
    <p className="text-zinc-200" data-testid="contact-hours-appointment">By appointment — call or message us</p>
  ) : (
    <table className="w-full max-w-sm text-sm" data-testid="contact-hours-table">
      <tbody>
        {s.hours.map((h) => (
          <tr key={h.day} className="border-b border-zinc-800 last:border-0" data-testid={`hours-row-${h.day.toLowerCase()}`}>
            <td className="py-2 pr-4 text-zinc-400">{h.day}</td>
            <td className="py-2 text-right text-zinc-100">{h.closed ? "Closed" : `${formatTime(h.open)} – ${formatTime(h.close)}`}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

const Block = ({ icon: Icon, title, children }) => (
  <div className="flex gap-4">
    <Icon className="mt-1 h-5 w-5 shrink-0 text-[#FFD23F]" />
    <div className="min-w-0 flex-1">
      <h3 className="font-sub text-sm font-semibold uppercase tracking-[0.2em] text-zinc-400">{title}</h3>
      <div className="mt-2">{children}</div>
    </div>
  </div>
);

export const ContactSection = ({ settings: s, services = [] }) => {
  const mapQuery = s.street_address || s.location || "Falcon, CO 80831";
  return (
    <section id="contact" className="section-anchor relative bg-[#121214]" data-testid="contact-section">
      <div className="stripe-rule" />
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24">
        <SectionHead eyebrow="Come see us" title="Contact" />
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_1.2fr]">
          <div className="space-y-9">
            <Block icon={Phone} title="Call us">
              <ul className="space-y-3">
                {s.contacts.map((c, i) => (
                  <li key={i} data-testid={`contact-person-${i}`}>
                    <a href={telHref(c.phone)} className="group flex flex-wrap items-baseline gap-x-2" data-testid={`contact-phone-link-${i}`}>
                      <span className="font-sub text-lg font-semibold uppercase text-[#FAF7F2]">{c.name}</span>
                      {c.title && <span className="text-sm text-zinc-400">— {c.title} —</span>}
                      <span className="font-display text-2xl text-[#FFD23F] transition-colors group-hover:text-[#F4EFE6]">{c.phone}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </Block>
            <Block icon={MapPin} title="Location">
              {s.street_address && <p className="text-lg text-[#FAF7F2]" data-testid="contact-street-address">{s.street_address}</p>}
              <p className="text-lg text-[#FAF7F2]" data-testid="contact-location">{s.location}</p>
              {s.location_note && <p className="mt-1 text-[#FFD23F]" data-testid="contact-location-note">{s.location_note}</p>}
            </Block>
            <Block icon={Clock} title="Hours"><Hours s={s} /></Block>
            {s.public_email && (
              <Block icon={Mail} title="Email">
                <a href={`mailto:${s.public_email}`} className="break-all text-lg text-[#FAF7F2] underline decoration-[#FFD23F] underline-offset-4" data-testid="contact-email-link">
                  {s.public_email}
                </a>
              </Block>
            )}
          </div>
          <div className="overflow-hidden rounded-md border border-zinc-800 bg-[#1A1A1E]">
            <iframe
              title="Map"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=13&output=embed`}
              className="h-[340px] w-full grayscale-[35%] sm:h-[460px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map"
            />
          </div>
        </div>
        <div className="mt-14">
          <MessageForm services={services} primaryPhone={s.primary_phone} />
        </div>
      </div>
    </section>
  );
};
